'use client';

import React, { useState, Fragment } from 'react';
import clx from 'classnames';

import useFormatFilterUi from './hooks/useFormatFilterUi';
import { useSortAndFilterConfigContext } from '../context/SortAndFilterConfigContext';
import { useTreatmentFilterStateContext } from './context/TreatmentFilterContext';

import SortAndFilterDialog from '../SortAndFilterDialog/SortAndFilterDialog';
import Button from '../../../_ui/_blocks/Buttons/Button/Button';
import Typography from '../../../_ui/_blocks/Typography/Typography';
import PriceFilter from './Filters/PriceFilter/PriceFilter';
import ConditionsFilter from './Filters/ConditionsFilter/ConditionsFilter';
import BrandsFilter from './Filters/BrandsFilter/BrandsFilter';
import ActiveIngredientFilter from './Filters/ActiveIngredientFilter/ActiveIngredientFilter';
import OverTheCounterFilter from './Filters/OverTheCounterFilter/OverTheCounterFilter';
import ReviewFilter from './Filters/ReviewFilter/ReviewFilter';
import TreatmentFormFilter from './Filters/TreatmentFormFilter/TreatmentFormFilter';
import DrugClassFilter from './Filters/DrugClassFilter/DrugClassFilter';
import FilterActions from './FilterActions/FilterActions';

import styles from './TreatmentFilter.module.css';
import Icon from '../../../_ui/_blocks/Icon/Icon';

const FilterUiComponents = {
    brand: BrandsFilter,
    conditions: ConditionsFilter,
    activeIngredient: ActiveIngredientFilter,
    treatmentForm: TreatmentFormFilter,
    drugClass: DrugClassFilter,
    price: PriceFilter,
    productType: OverTheCounterFilter,
    review: ReviewFilter,
};

interface Props {
    className?: string;
}

const TreatmentFilter = ({ className }: Props) => {
    const classNames = clx(styles.button, className);

    const config = useSortAndFilterConfigContext();
    const { numberOfFiltersApplied } = useTreatmentFilterStateContext();

    const formattedFilterUi = useFormatFilterUi(config);

    const [isOpen, setIsOpen] = useState(false);
    const handleClose = () => setIsOpen(false);
    const handleOpen = () => setIsOpen(true);

    return (
        <Fragment>
            <SortAndFilterDialog isOpen={isOpen} onClose={handleClose} title="Filters" size="window">
                <div className={styles.filters}>
                    {formattedFilterUi.map((filter) => {
                        const ComponentToRender = FilterUiComponents[filter];
                        return ComponentToRender ? <ComponentToRender key={filter} /> : null;
                    })}
                </div>
                <FilterActions onClose={handleClose} />
            </SortAndFilterDialog>
            <Button
                variant="tertiary"
                size="micro"
                onClick={handleOpen}
                title="Change filtering"
                className={`${classNames} md:button-size-small`}
                data-testid="treatment-filter-button"
            >
                Filter By
                {numberOfFiltersApplied ? (
                    <Typography color="white" size="090" typeset="emphasis" className={styles.filterCount} lineHeight="none">
                        {numberOfFiltersApplied}
                    </Typography>
                ) : null}
                <Icon icon="sort-light" alt='funnel icon' size="xsmall" />
            </Button>
        </Fragment>
    );
};

export default TreatmentFilter;
